import React from 'react';
import { WifiOff, RefreshCw, ServerCrash } from 'lucide-react';
import { useConnectivity } from '../../hooks/useConnectivity';

/**
 * Persistent connectivity banner.
 *
 * Unlike a transient toast, this stays visible as long as the app cannot reach
 * the server (or the device is offline) and disappears the moment a real API
 * call succeeds again. It owns the top edge of the content area so it never
 * competes with sticky action bars (z-30) or dialogs (z-50).
 *
 * Per the design system: offline/sync states must be persistent, not transient,
 * and state must never be implied when it is not true.
 */
const NetworkStatusBanner: React.FC = () => {
  const { status } = useConnectivity();

  if (status === 'online') {
    return null;
  }

  const isOffline = status === 'offline';

  return (
    <div
      role='status'
      aria-live='polite'
      data-testid='network-status-banner'
      className={
        'flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white ' +
        (isOffline ? 'bg-gray-800' : 'bg-red-600')
      }
    >
      {isOffline ? (
        <WifiOff className='h-4 w-4 shrink-0' aria-hidden='true' />
      ) : (
        <ServerCrash className='h-4 w-4 shrink-0' aria-hidden='true' />
      )}
      <span className='text-center min-w-0'>
        {isOffline
          ? 'ไม่มีการเชื่อมต่ออินเทอร์เน็ต — ข้อมูลบางส่วนอาจไม่เป็นปัจจุบัน'
          : 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ — กำลังพยายามเชื่อมต่อใหม่'}
      </span>
      {!isOffline && (
        <RefreshCw
          className='h-3.5 w-3.5 shrink-0 animate-spin'
          aria-hidden='true'
        />
      )}
    </div>
  );
};

export default NetworkStatusBanner;
